import { AssignableRole } from 'src/common/roleRegistry/roleRegistry';
import {
  useUserManagementAPI,
  UseUserManagementAPIReturn,
} from 'src/SharedCustomHooks/UseUserManagementAPI/useUserManagementAPI';
import { RoleChangeCallout } from 'src/components/ContactFormComponents/ChangeUserRoleDialogue/utils/deriveRoleChangeCallouts';
import {
  useChangeRoleFormState,
  UseChangeRoleFormStateReturn,
} from 'src/components/ContactFormComponents/ChangeUserRoleDialogue/CustomHooks/useChangeRoleFormState';

export interface UseChangeUserRoleOrchestratorArgs {
  contactId: string;
  currentRole: string;
  onSuccess?: (newRole: AssignableRole) => void;
}

export interface UseChangeUserRoleOrchestratorReturn {
  availableRoles: readonly AssignableRole[];
  selectedRole: AssignableRole | null;
  setSelectedRole: (role: AssignableRole) => void;
  callouts: RoleChangeCallout[];
  submit: () => Promise<void>;
  reset: () => void;
  isSubmitting: boolean;
  isValid: boolean;
  error: string | null;
  successMessage: string | null;
}

export const useChangeUserRoleOrchestrator = ({
  contactId,
  currentRole,
  onSuccess,
}: UseChangeUserRoleOrchestratorArgs): UseChangeUserRoleOrchestratorReturn => {
  const userManagementAPI: UseUserManagementAPIReturn = useUserManagementAPI();

  const formState: UseChangeRoleFormStateReturn =
    useChangeRoleFormState(currentRole);

  const submit = async (): Promise<void> => {
    if (!formState.isValid || formState.selectedRole === null) {
      return;
    }

    const newRole: AssignableRole = formState.selectedRole;

    const ok: boolean = await userManagementAPI.changeUserRole(
      contactId,
      newRole
    );

    if (ok && onSuccess) {
      onSuccess(newRole);
    }
  };

  const reset = () => {
    formState.reset();
    userManagementAPI.clearError();
    userManagementAPI.clearSuccess();
  };

  return {
    availableRoles: formState.availableRoles,
    selectedRole: formState.selectedRole,
    setSelectedRole: formState.setSelectedRole,
    callouts: formState.callouts,
    submit,
    reset,
    isSubmitting: userManagementAPI.isSubmitting,
    isValid: formState.isValid,
    error: userManagementAPI.error,
    successMessage: userManagementAPI.successMessage,
  };
};
